import {Component, ElementRef, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild, ViewContainerRef} from '@angular/core';
import {FloatLabelType} from '@angular/material/form-field';
import {ValidationService} from '../../services/validation/validation.service';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {TranslateService} from '@ngx-translate/core';
import {MatChipInputEvent} from '@angular/material/chips';
import {cloneDeep} from 'lodash-es';
import {StorageService} from "../../services/storage/storage.service";

@Component({
  selector: 'app-chip-list',
  templateUrl: './chip-list.component.html',
  styleUrls: ['./chip-list.component.scss']
})
export class ChipListComponent implements OnInit {
  @ViewChild('chipListComponent', {static: true}) chipListComponent: TemplateRef<HTMLInputElement>;
  @ViewChild('chipListInput') chipInput: ElementRef<HTMLInputElement>;

  @Input() label: string;
  @Input() customClass: string;
  @Input() noHint: boolean;
  @Input() items: (string | number)[] = [];
  @Input() valid: boolean = true;
  @Input() errorMessage: string;
  @Input() placeHolder: string = 'general.api_chips_add_new';
  @Input() allowClear: boolean;
  @Input() disabled: boolean;
  @Input() minLength: number = 3;
  @Input() maxLength: number;
  @Input() maxAmount: number;
  @Input() regex: RegExp;
  @Input() storageKey: string;
  @Input() displayInput: boolean = true;

  @Output() itemsChange: EventEmitter<(string | number)[]> = new EventEmitter<(string | number)[]>();
  @Output() itemAdded: EventEmitter<(string | number)> = new EventEmitter<(string | number)>();
  @Output() itemRemoved: EventEmitter<(string | number)> = new EventEmitter<(string | number)>();
  @Output() beforeClear: EventEmitter<any> = new EventEmitter<any>();
  @Output() clear: EventEmitter<any> = new EventEmitter<any>();
  @Output() touched: EventEmitter<any> = new EventEmitter<any>();

  public separatorKeysCodes: number[] = [ENTER, COMMA];
  public chipListFloatLabelValue: FloatLabelType = 'auto';
  public invalidLength: boolean;
  public valueExists: boolean;
  public invalidFormat: boolean;
  public currentChip: string;

  constructor(private translateService: TranslateService,
              private viewContainerRef: ViewContainerRef,
              private storageService: StorageService) {}

  ngOnInit(): void {
    this.viewContainerRef.createEmbeddedView(this.chipListComponent);
    this.getFromStorage();
  }

  isItemValid(item: string): boolean {
    return !this.regex || !!item.match(this.regex);
  }

  saveToStorage(): void {
    if (this.storageKey?.length) {
      this.storageService.set('chipList.' + this.storageKey, JSON.stringify(this.items));
    }
  }

  getFromStorage(): void {
    if (this.storageKey?.length) {
      const baseItems: (string | number)[] = cloneDeep(this.items);
      const query: string = this.storageService.get('chipList.' + this.storageKey);
      if (query?.length) {
        this.items = JSON.parse(query);
        if (JSON.stringify(baseItems) !== JSON.stringify(this.items)) {
          this.itemsChange.emit(this.items);
        }
      }
    }
  }

  checkRegex(event: any): void {
    this.invalidFormat = false;
    if (event && event.target) {
      this.invalidFormat = !this.isItemValid(event.target.value);
    }
  }

  addChip(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (!value?.length) {
      event.chipInput.clear();
      return;
    }
    if (!this.items) {
      this.items = [];
    }
    if (this.maxAmount && this.items.length >= this.maxAmount) {
      event.chipInput.clear();
      return;
    }
    if (value && ValidationService.validateString(value, this.minLength, this.maxLength)) {
      this.invalidLength = false;
      if (this.items.includes(value)) {
        this.valueExists = true;
        setTimeout(() => {
          this.valueExists = false;
        }, 5000);
      } else {
        this.valueExists = false;
        if (this.isItemValid(value)) {
          this.items.push(value);
          this.saveToStorage();
          this.itemsChange.emit(this.items);
          this.itemAdded.emit(value);
        }
      }
    } else {
      this.invalidLength = true;
      this.valueExists = false;
      setTimeout(() => {
        this.invalidLength = false;
      }, 5000);
    }
    event.chipInput.clear();
  }

  removeChip(item: string): void {
    this.items.splice(this.items.indexOf(item), 1);
    this.saveToStorage();
    this.itemsChange.emit(this.items);
    this.itemRemoved.emit(item);
    this.invalidFormat = false;
    if (!this.items.length) {
      this.invalidLength = false;
      this.valueExists = false;
    }
  }

  removeAll(): void {
    this.beforeClear.emit(cloneDeep(this.items));
    this.items = [];
    this.saveToStorage();
    this.itemsChange.emit(this.items);
    this.clear.emit();
    this.invalidLength = false;
    this.invalidFormat = false;
    this.valueExists = false;
  }

  public get hideHint(): boolean {
    return this.noHint && this.valid && !this.invalidLength && !this.valueExists && !this.invalidFormat
  }
}
